// @intent: Typed, dependency-free match-3 level contract for AI-produced native payloads.
// @flow: W6.3 typed input for single-engine, single-platform native match3 runtime.
// @stale: Keep this file narrow: no zod, no external imports, runtime-light validator only.

export type Match3TileIdV1 = string;
export type Match3LevelVersionV1 = 'v1';
export type Match3FamilyV1 = 'match3';

export interface Match3CoordinateV1 {
  readonly row: number;
  readonly col: number;
}

export interface Match3AssetFitV1 {
  readonly mode: 'cover' | 'contain' | 'fill' | 'none';
  readonly anchor: Match3PointV1;
}

export interface Match3PointV1 {
  readonly x: number;
  readonly y: number;
}

export interface Match3RectV1 {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Match3TileDefinitionV1 {
  readonly id: Match3TileIdV1;
  readonly assetUrl: string;
  readonly swappable?: boolean;
  readonly assetFit?: Match3AssetFitV1;
}

export type Match3StaticCellClearRuleV1 = 'adjacent-match' | 'matched-cell' | 'scripted';

export interface Match3StaticCellV1 {
  readonly at: Match3CoordinateV1;
  readonly tileId: Match3TileIdV1;
  readonly swappable: false;
  readonly role?: string;
  readonly stage?: number;
  readonly maxStage?: number;
  readonly clearRule?: Match3StaticCellClearRuleV1;
}

export interface Match3BlockerV1 {
  readonly id: string;
  readonly at: Match3CoordinateV1;
  readonly kind: 'wall' | 'obstacle' | 'void';
  readonly swappable: boolean;
  readonly fillsCell?: boolean;
  readonly fillTileId?: Match3TileIdV1;
}

export type Match3BoardCellV1 = Match3TileIdV1 | null;

/**
 * @intent One collectible underlay cell — a swappable tile resting on a jelly/wafer underlay that is collected when
 *   the tile on that cell is cleared by a match. The tile itself stays swappable (the underlay is the objective).
 * @flow board.collectibleUnderlays → engine seeds an underlayByIndex map; cleared tile index → underlay collected →
 *   matching collectN objective advances by 1 per collected underlay role.
 * @stale V1.1 additive — absent = current behavior (no underlay cells). Equivalent to Candy-Crush jelly mechanic.
 */
export interface Match3CollectibleUnderlayV1 {
  /** Board position of the underlay cell. The tile at this cell remains swappable. */
  readonly at: Match3CoordinateV1;
  /** Objective role string matched against objective.role for collectN counting. Must be non-empty. */
  readonly role: string;
}

export interface Match3BoardV1 {
  readonly rows: number;
  readonly cols: number;
  readonly cells: readonly Match3BoardCellV1[];
  readonly enabledMask?: readonly boolean[];
  readonly blockers?: readonly Match3BlockerV1[];
  readonly staticCells?: readonly Match3StaticCellV1[];
  /**
   * @intent Additive optional list of collectible underlay cells (jelly/wafer mechanic).
   * @flow Engine seeds a mutable Map<index,role> from this array; each cleared tile on an underlay cell collects it.
   * @stale V1.1 additive — absent = no underlay cells, existing behavior preserved.
   */
  readonly collectibleUnderlays?: readonly Match3CollectibleUnderlayV1[];
}

export interface Match3LayoutSizingV1 {
  readonly boardRect: Match3RectV1;
  readonly tileSizePx: number;
  readonly tileGapPx: number;
}

export interface Match3LevelLayoutV1 {
  readonly viewportWidthPx: number;
  readonly viewportHeightPx: number;
  readonly sizing: Match3LayoutSizingV1;
}

export type Match3ObjectiveTypeV1 = 'collectN' | 'scoreTarget' | 'pvpRace' | 'grow';

export interface Match3ObjectiveV1 {
  readonly type: Match3ObjectiveTypeV1;
  readonly target: number;
  readonly role?: string;
}

export type Match3LimitV1 =
  | { readonly kind: 'moves'; readonly value: number }
  | { readonly kind: 'timer'; readonly seconds: number }
  | { readonly kind: 'turns'; readonly value: number }
  | { readonly kind: 'none' };

export interface Match3ProbeExpectationsV1 {
  readonly boardTileCount?: boolean;
  readonly objectiveRemaining?: boolean;
  readonly ctaShown?: boolean;
  readonly nextMoveHint?: boolean;
}

export interface Match3TelemetryV1 {
  readonly enabled?: boolean;
  readonly requiredEvents?: readonly string[];
  readonly probes?: {
    readonly expectations?: Match3ProbeExpectationsV1;
  };
}

export interface Match3ScriptMoveV1 {
  readonly a: Match3CoordinateV1;
  readonly b: Match3CoordinateV1;
}

export interface Match3LevelScriptV1 {
  readonly firstMove: Match3ScriptMoveV1;
  readonly winningMoves: readonly Match3ScriptMoveV1[];
  readonly allowFreePlayAfterScript?: boolean;
}

export interface Match3TutorialV1 {
  readonly masks?: readonly (readonly number[])[];
  readonly hand?: boolean;
  readonly copy?: string;
  readonly firstMoveOnly?: boolean;
}

export interface Match3PacingV1 {
  readonly swapMs?: number;
  readonly clearMs?: number;
  readonly gravityMs?: number;
  readonly refillDelayMs?: number;
  readonly objectiveFlyMs?: number;
}

export type Match3PvpModeV1 = 'pvp' | 'pve';
export type Match3PvpTurnV1 = 'player' | 'opponent';

export interface Match3PvpConfigV1 {
  readonly mode?: Match3PvpModeV1;
  readonly firstTurn?: Match3PvpTurnV1;
  readonly preplayedMove?: boolean;
  readonly roundLength?: number;
  readonly playerMoveCount?: number;
  readonly opponentMoveCount?: number;
  readonly difficulty?: number;
  readonly playerTarget?: number;
  readonly opponentTarget?: number;
  readonly aiThinkMs?: number;
  readonly aiFollowUpMs?: number;
  readonly playerBooster?: string;
  readonly opponentBooster?: string;
}

export type Match3SpecialSpawnV1 = 'none' | 'scripted' | 'firstMove' | 'cascade' | 'random';

export interface Match3SpecialConfigV1 {
  readonly id: string;
  readonly kind: string;
  readonly spawn?: Match3SpecialSpawnV1;
  readonly effect?: string;
  readonly guaranteeOnFirstMove?: boolean;
  // @intent Pre-placed position for the special tile (additive; absent = dynamically spawned via match rules).
  // @flow V1.1 E3 (Б4): builder places one special near scripted first move; engine reads `at` to seed specialByIndex on init.
  // @stale V1.1 Б1 additive.
  readonly at?: Match3CoordinateV1;
}

export type Match3CollectFlightTargetV1 = 'objectiveHud' | 'scoreHud' | 'progressHud';

export interface Match3CollectFlightFeedbackV1 {
  readonly enabled?: boolean;
  readonly sourceRole?: string;
  readonly target: Match3CollectFlightTargetV1;
  readonly durationMs: number;
  readonly trail?: boolean;
}

export interface Match3FeedbackV1 {
  readonly collectFlight?: Match3CollectFlightFeedbackV1;
}

export interface Match3LevelV1 {
  readonly version: Match3LevelVersionV1;
  readonly family: Match3FamilyV1;
  readonly topologyId: string;
  readonly topologyHash: string;
  readonly runtimeSeed?: number;
  readonly board: Match3BoardV1;
  readonly tiles: readonly Match3TileDefinitionV1[];
  readonly layout: Match3LevelLayoutV1;
  readonly objective: Match3ObjectiveV1;
  readonly limit: Match3LimitV1;
  readonly script?: Match3LevelScriptV1;
  readonly telemetry?: Match3TelemetryV1;
  readonly tutorial?: Match3TutorialV1;
  readonly pacing?: Match3PacingV1;
  readonly pvp?: Match3PvpConfigV1;
  readonly specials?: readonly Match3SpecialConfigV1[];
  readonly feedback?: Match3FeedbackV1;
}

export type Match3SkinPerspectiveV1 = 'front' | 'slight-top-down' | 'isometric-lite';
export type Match3SkinTileShapeV1 = 'rounded-square-token' | 'object-cutout' | 'coin-like' | 'jelly-like';
export type Match3SkinBackgroundPolicyV1 = 'quiet-depth' | 'soft-context' | 'plain-gradient-forbidden';

export interface Match3SkinStyleBibleV1 {
  readonly styleName: string;
  readonly palette: readonly string[];
  readonly lighting: string;
  readonly perspective: Match3SkinPerspectiveV1;
  readonly tileShape: Match3SkinTileShapeV1;
  readonly backgroundPolicy: Match3SkinBackgroundPolicyV1;
}

export interface Match3SkinSlotsV1 {
  readonly background: string;
  readonly boardPanel: string;
  readonly tileTypes: readonly string[];
  readonly objectiveStages: readonly string[];
  readonly tutorialHand: string;
  readonly hudPanel: string;
  readonly ctaButton: string;
  readonly logo?: string;
  readonly reward?: string;
  /** @intent Textures for staged blockers (ice/rock), index 0 = intact, index N = most damaged. */
  /** @flow Used by renderer to pick texture based on staticCell.stage; absent = no blockers renderable. */
  /** @stale Update when new blocker types (multi-cell, fire) are added in Wave В. */
  readonly blockerStages?: readonly string[];
  /**
   * @intent Intrinsic pixel size of the background texture for true cover-fit math.
   * @flow renderer: scale = max(vpW/w, vpH/h), center, uniform scale → crop overflow.
   *   Absent = legacy stretch to vpW×vpH (no cover math, may letterbox on non-matching aspect).
   * @stale V1.2 additive — absent = existing stretch behavior (back-compat with all prior skins).
   */
  readonly backgroundSize?: { readonly w: number; readonly h: number };
}

export interface Match3SkinFitV1 {
  readonly tileCellFill: number;
  readonly tilePaddingPx: number;
  readonly boardInsetPx: number;
  readonly anchor: Match3PointV1;
}

/**
 * @intent Per-cell alternating checker tint — replaces dark socket underlays with warm two-tone rects.
 * @flow renderer reads colorA/colorB/alpha to fill (row+col)%2 cells; absent = dark socket default.
 *   'none' string = skip ALL per-cell socket/checker drawing entirely (no underlays rendered).
 * @stale Update if a third checker pattern (stripe/gradient) is needed in future wave.
 */
export interface Match3SkinCellCheckerV1 {
  readonly colorA: string;
  readonly colorB: string;
  readonly alpha: number;
}

/** 'none' sentinel — skip all per-cell socket/checker drawing. */
export type Match3SkinCellCheckerModeV1 = Match3SkinCellCheckerV1 | 'none';

/**
 * @intent Bottom banner sprite shown before first interaction; fades out on first accepted move.
 * @flow renderer mounts NativeSprite centered at viewport bottom; hides after firstInteraction.
 * @stale Update heightPx range if portrait aspect changes significantly (current 390×844 baseline).
 */
export interface Match3SkinBannerV1 {
  readonly assetUrl: string;
  readonly heightPx: number;
}

/**
 * @intent Single ambient decoration sprite with idle bob tween (y ±6px looping).
 * @flow renderer creates NativeSprite `match-ambient-N`; bob tween re-triggers on completion.
 * @stale Keep ambientSprites count ≤ 6; probe classification depends on match-ambient- prefix.
 */
export interface Match3SkinAmbientSpriteV1 {
  readonly assetUrl: string;
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

/**
 * @intent Additive optional presentation overrides for skin art direction.
 *   Absence of any field = exact current renderer behavior (back-compat; existing fixtures untouched).
 * @flow skin.presentation -> renderer reads each field at render time; no engine/runtime changes.
 * @stale Precedent: blockerStages (V1.1 Б3 additive optional). Cross-ref Match3NativeRenderer.tsx
 *   presentation rendering block; keep field list in sync.
 */
export interface Match3SkinPresentationV1 {
  /** 'plain' = current programmatic dark panel (default); 'none' = skip panel graphic entirely. */
  readonly boardPanel?: 'plain' | 'none';
  /**
   * Per-cell alternating tint instead of dark sockets; absent = dark socket default.
   * 'none' string = skip ALL per-cell socket/checker drawing (no underlays rendered at all).
   */
  readonly cellChecker?: Match3SkinCellCheckerModeV1;
  /** false = suppress HUD pill/icon/counter/title; default true. */
  readonly hudVisible?: boolean;
  /** Bottom banner shown before first interaction, fades on first accepted move. */
  readonly banner?: Match3SkinBannerV1;
  /** Decor sprites with idle bob tween (y ±6px, ~1200ms, ease in-out, infinite). */
  readonly ambientSprites?: readonly Match3SkinAmbientSpriteV1[];
  /**
   * true = render soft per-cell drop-shadow pass between socket underlays and tile sprites.
   * Shadow is a dark rounded-rect offset (+3px x, +4px y) with alpha ~0.22. Default false.
   * @flow renderer draws shadow pass only when tileShadow===true (gate: additive opt-in).
   * @stale V1.2 additive — absent = no shadow (back-compat with all existing skins).
   */
  readonly tileShadow?: boolean;
}

export interface Match3SkinPackV1 {
  readonly version: Match3LevelVersionV1;
  readonly id: string;
  readonly sourceReferenceId?: string;
  readonly styleBible: Match3SkinStyleBibleV1;
  readonly slots: Match3SkinSlotsV1;
  readonly fit: Match3SkinFitV1;
  /** @intent Additive optional art-direction overrides; absent = default renderer behavior. */
  /** @flow renderer reads each field independently; missing field = fallback to current default. */
  /** @stale Keep in sync with Match3SkinPresentationV1 and Match3NativeRenderer presentation block. */
  readonly presentation?: Match3SkinPresentationV1;
}

export interface Match3NativeRuntimePayloadV1 {
  readonly version: Match3LevelVersionV1;
  readonly level: Match3LevelV1;
  readonly skin: Match3SkinPackV1;
}

export type Match3ProbeBoardCellV1 = Match3TileIdV1 | number | null;

export interface Match3ProbeCellRectV1 {
  readonly tileIndex: number;
  readonly row: number;
  readonly col: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type Match3ProbeObjectiveFlightStatusV1 = 'scheduled' | 'running' | 'finished';

export interface Match3ProbeObjectiveFlightV1 {
  readonly sourceIndex: number;
  readonly row: number;
  readonly col: number;
  readonly source: Match3RectV1;
  readonly target: Match3RectV1;
  readonly durationMs: number;
  readonly status: Match3ProbeObjectiveFlightStatusV1;
}

export interface Match3ProbeTupleMoveV1 {
  readonly from: readonly [number, number];
  readonly to: readonly [number, number];
}

export interface Match3ProbeIndexMoveV1 {
  readonly fromIndex: number;
  readonly toIndex: number;
}

export type Match3ResolutionPhaseV1 = 'idle' | 'swap' | 'clear' | 'refill' | 'complete';

export interface Match3ProbeResolutionProgressV1 {
  readonly scoreDelta: number;
  readonly score: number;
  readonly collected: number;
  readonly objectiveRemaining: number;
}

// @intent One special-tile entry on the board (kind = 'rocket' | 'bomb' | 'colorBomb'; index = flat row-major position).
// @flow Populated by spawn rules after a 4/5/L/T match; consumed by the renderer for overlay visuals and by the engine for detonation.
// @stale V1.1 Б1 additive — absent = no specials on board.
export interface Match3ProbeSpecialEntryV1 {
  readonly index: number;
  readonly kind: string;
}

export interface Match3ProbeResolutionCascadeV1 {
  readonly phase: 'clear-refill';
  readonly matchedIndexes: readonly number[];
  readonly boardAfterClear: readonly Match3ProbeBoardCellV1[];
  readonly cleared: number;
  readonly progress: Match3ProbeResolutionProgressV1;
  readonly refillChangedIndexes: readonly number[];
  readonly boardAfterRefill: readonly Match3ProbeBoardCellV1[];
  readonly repaintIndexes: readonly number[];
  // @intent Specials spawned in this cascade step (additive; absent = none spawned).
  // @flow Filled by spawn rules when a run of length ≥4 is detected; renderer uses this to draw overlay.
  // @stale V1.1 Б1 additive.
  readonly spawnedSpecials?: readonly Match3ProbeSpecialEntryV1[];
  // @intent Specials detonated in this cascade step (additive; absent = none detonated).
  // @flow Filled when a blast clears a cell that held a special tile; chaining is recorded per cascade step.
  // @stale V1.1 Б1 additive.
  readonly detonatedSpecials?: readonly Match3ProbeSpecialEntryV1[];
}

export interface Match3ProbeResolutionPlanV1 {
  readonly result: 'accepted' | 'rejected';
  readonly fromIndex: number;
  readonly toIndex: number;
  readonly acceptedMove: Match3ProbeIndexMoveV1 | null;
  readonly rejectedMove: Match3ProbeIndexMoveV1 | null;
  readonly initialMatchedIndexes: readonly number[];
  readonly postSwapBoard: readonly Match3ProbeBoardCellV1[];
  readonly cascades: readonly Match3ProbeResolutionCascadeV1[];
  readonly repaintIndexes: readonly number[];
  readonly finalBoard: readonly Match3ProbeBoardCellV1[];
  readonly outcome: 'win' | 'lose' | 'none';
  // @intent All specials spawned across the full move resolution (additive; absent = none spawned).
  // @flow Aggregated from all cascade steps; used by the renderer and probe validator.
  // @stale V1.1 Б1 additive.
  readonly spawnedSpecials?: readonly Match3ProbeSpecialEntryV1[];
  // @intent All specials detonated across the full move resolution (additive; absent = none detonated).
  // @flow Aggregated from all cascade steps; used by the renderer and probe validator.
  // @stale V1.1 Б1 additive.
  readonly detonatedSpecials?: readonly Match3ProbeSpecialEntryV1[];
}

export interface Match3ProbeStateV1 {
  readonly rows: number;
  readonly cols: number;
  readonly board: readonly Match3ProbeBoardCellV1[];
  readonly firstMove?: Match3ProbeTupleMoveV1;
  readonly nextMove?: Match3ProbeTupleMoveV1;
  readonly cellRects: readonly Match3ProbeCellRectV1[];
  readonly tileLayerIds: readonly string[];
  readonly lastAcceptedMove?: Match3ProbeIndexMoveV1;
  readonly acceptedMoveSeq?: number;
  readonly lastRejectedMove?: Match3ProbeIndexMoveV1;
  readonly rejectedMoveSeq?: number;
  readonly lastRepaintIndexes?: readonly number[];
  readonly lastResolutionPlan?: Match3ProbeResolutionPlanV1;
  readonly staticCellIndexes?: readonly number[];
  readonly tutorialMaskIndexes?: readonly number[];
  readonly objectiveFlights?: readonly Match3ProbeObjectiveFlightV1[];
  readonly resolutionPhase?: Match3ResolutionPhaseV1;
  readonly objectiveRemaining: number;
  readonly flowerStageProgress?: number;
  readonly tileCount: number;
  readonly resolving: boolean;
  readonly ctaShown: boolean;
  // @intent Live special tiles currently on the board (additive; absent = no specials present).
  // @flow Updated after each spawn/detonation; renderer reads this to draw special tile overlays.
  // @stale V1.1 Б1 additive.
  readonly specials?: readonly Match3ProbeSpecialEntryV1[];
  // @intent Remaining collectible underlay cells (additive; absent = no underlays or all collected).
  // @flow Engine removes entries as underlays are collected; renderer uses this to draw jelly/wafer underlay sprites.
  // @stale V1.1 additive — absent = no underlay mechanic in use.
  readonly collectibleUnderlays?: readonly { readonly index: number; readonly role: string }[];
}

export type Match3LevelValidationResult =
  | { readonly ok: true; readonly value: Match3LevelV1 }
  | { readonly ok: false; readonly errors: string[] };

export type Match3ProbeStateValidationResult =
  | { readonly ok: true; readonly value: Match3ProbeStateV1 }
  | { readonly ok: false; readonly errors: string[] };

export type Match3SkinPackValidationResult =
  | { readonly ok: true; readonly value: Match3SkinPackV1 }
  | { readonly ok: false; readonly errors: string[] };

export type Match3NativeRuntimePayloadValidationResult =
  | { readonly ok: true; readonly value: Match3NativeRuntimePayloadV1 }
  | { readonly ok: false; readonly errors: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown, path: string, errors: string[]): string | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    errors.push(`${path} must be a non-empty string`);
    return undefined;
  }
  return value;
}

function asBoolean(value: unknown, path: string, errors: string[]): boolean | undefined {
  if (typeof value !== 'boolean') {
    errors.push(`${path} must be boolean`);
    return undefined;
  }
  return value;
}

function asInteger(value: unknown, path: string, errors: string[], min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
    errors.push(`${path} must be an integer`);
    return undefined;
  }
  if (value < min || value > max) {
    errors.push(`${path} must be between ${String(min)} and ${String(max)}`);
    return undefined;
  }
  return value;
}

function asNumber(value: unknown, path: string, errors: string[], min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${path} must be a finite number`);
    return undefined;
  }
  if (value < min || value > max) {
    errors.push(`${path} must be between ${String(min)} and ${String(max)}`);
    return undefined;
  }
  return value;
}

function asArray(value: unknown, path: string, errors: string[]): unknown[] | undefined {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return undefined;
  }
  return Array.from(value as readonly unknown[]);
}

function isPoint(value: unknown, path: string, errors: string[]): Match3PointV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a point`);
    return undefined;
  }
  const x = asNumber(value.x, `${path}.x`, errors);
  const y = asNumber(value.y, `${path}.y`, errors);
  if (x === undefined || y === undefined) return undefined;
  return { x, y };
}

function isRect(value: unknown, path: string, errors: string[]): Match3RectV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a rect`);
    return undefined;
  }
  const x = asNumber(value.x, `${path}.x`, errors);
  const y = asNumber(value.y, `${path}.y`, errors);
  const width = asNumber(value.width, `${path}.width`, errors, 0);
  const height = asNumber(value.height, `${path}.height`, errors, 0);
  if (x === undefined || y === undefined || width === undefined || height === undefined) return undefined;
  if (width <= 0) errors.push(`${path}.width must be > 0`);
  if (height <= 0) errors.push(`${path}.height must be > 0`);
  return { x, y, width, height };
}

function asCoordinate(value: unknown, path: string, rows: number, cols: number, errors: string[]): Match3CoordinateV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a coordinate`);
    return undefined;
  }
  const row = asInteger(value.row, `${path}.row`, errors, 0);
  const col = asInteger(value.col, `${path}.col`, errors, 0);
  if (row === undefined || col === undefined) return undefined;
  if (rows > 0 && (row >= rows || col >= cols)) {
    errors.push(`${path} must be within bounds`);
  }
  return { row, col };
}

function asScriptMove(value: unknown, path: string, rows: number, cols: number, errors: string[]): Match3ScriptMoveV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a move object`);
    return undefined;
  }
  const a = asCoordinate(value.a, `${path}.a`, rows, cols, errors);
  const b = asCoordinate(value.b, `${path}.b`, rows, cols, errors);
  if (a === undefined || b === undefined) return undefined;
  return { a, b };
}

function asAssetFit(value: unknown, path: string, errors: string[]): Match3AssetFitV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push(`${path} must be an asset fit object`);
    return undefined;
  }
  const mode = asString(value.mode, `${path}.mode`, errors) as Match3AssetFitV1['mode'] | undefined;
  if (mode !== undefined && !['cover', 'contain', 'fill', 'none'].includes(mode)) {
    errors.push(`${path}.mode must be cover|contain|fill|none`);
  }
  const anchor = isPoint(value.anchor, `${path}.anchor`, errors);
  if (anchor !== undefined) {
    if (anchor.x < 0 || anchor.x > 1) errors.push(`${path}.anchor.x must be in [0,1]`);
    if (anchor.y < 0 || anchor.y > 1) errors.push(`${path}.anchor.y must be in [0,1]`);
  }
  if (mode === undefined || anchor === undefined) return undefined;
  return { mode, anchor };
}

function indexOfCell(rows: number, cols: number, row: number, col: number): number {
  return row * cols + col;
}

function cloneBoardCells(cells: readonly Match3BoardCellV1[]): Match3BoardCellV1[] {
  return cells.map((cell) => cell);
}

function fail(errors: string[]): Match3LevelValidationResult {
  return { ok: false, errors };
}

function failProbe(errors: string[]): Match3ProbeStateValidationResult {
  return { ok: false, errors };
}

function failSkin(errors: string[]): Match3SkinPackValidationResult {
  return { ok: false, errors };
}

function failPayload(errors: string[]): Match3NativeRuntimePayloadValidationResult {
  return { ok: false, errors };
}

function pushError(errors: string[], message: string, already = new Set<string>()): void {
  if (already.has(message)) return;
  already.add(message);
  errors.push(message);
}

function asStringArray(
  value: unknown,
  path: string,
  errors: string[],
  minLength: number,
  maxLength: number,
  label: string,
): string[] | undefined {
  const raw = asArray(value, path, errors);
  if (!raw) return undefined;
  if (raw.length < minLength || raw.length > maxLength) {
    errors.push(`${path} must include ${label}`);
  }
  const values: string[] = [];
  for (let index = 0; index < raw.length; index += 1) {
    const stringValue = asString(raw[index], `${path}[${String(index)}]`, errors);
    if (stringValue !== undefined) values.push(stringValue);
  }
  return values;
}

function asEnum<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[],
  errors: string[],
): T | undefined {
  const stringValue = asString(value, path, errors);
  if (stringValue === undefined) return undefined;
  if (!(allowed as readonly string[]).includes(stringValue)) {
    errors.push(`${path} must be ${allowed.join('|')}`);
    return undefined;
  }
  return stringValue as T;
}

function isHexColor(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

export function validateMatch3SkinPackV1(skin: unknown): Match3SkinPackValidationResult {
  const errors: string[] = [];

  if (!isRecord(skin)) return failSkin(['skin must be an object']);
  const raw = skin;

  const version = asString(raw.version, 'version', errors);
  if (version !== 'v1') errors.push(`version must equal 'v1'`);
  const id = asString(raw.id, 'id', errors);
  const sourceReferenceId =
    raw.sourceReferenceId === undefined ? undefined : asString(raw.sourceReferenceId, 'sourceReferenceId', errors);

  const styleRaw = raw.styleBible;
  if (!isRecord(styleRaw)) {
    errors.push('styleBible must be an object');
    return failSkin(errors);
  }
  const styleName = asString(styleRaw.styleName, 'styleBible.styleName', errors);
  const palette = asStringArray(styleRaw.palette, 'styleBible.palette', errors, 3, 8, '3 to 8 hex colors');
  if (palette) {
    palette.forEach((color, index) => {
      if (!isHexColor(color)) {
        errors.push(`styleBible.palette[${String(index)}] must be a #RRGGBB color`);
      }
    });
  }
  const lighting = asString(styleRaw.lighting, 'styleBible.lighting', errors);
  const perspective = asEnum<Match3SkinPerspectiveV1>(
    styleRaw.perspective,
    'styleBible.perspective',
    ['front', 'slight-top-down', 'isometric-lite'],
    errors,
  );
  const tileShape = asEnum<Match3SkinTileShapeV1>(
    styleRaw.tileShape,
    'styleBible.tileShape',
    ['rounded-square-token', 'object-cutout', 'coin-like', 'jelly-like'],
    errors,
  );
  const backgroundPolicy = asEnum<Match3SkinBackgroundPolicyV1>(
    styleRaw.backgroundPolicy,
    'styleBible.backgroundPolicy',
    ['quiet-depth', 'soft-context', 'plain-gradient-forbidden'],
    errors,
  );

  const slotsRaw = raw.slots;
  if (!isRecord(slotsRaw)) {
    errors.push('slots must be an object');
    return failSkin(errors);
  }
  const background = asString(slotsRaw.background, 'slots.background', errors);
  const boardPanel = asString(slotsRaw.boardPanel, 'slots.boardPanel', errors);
  const tileTypes = asStringArray(slotsRaw.tileTypes, 'slots.tileTypes', errors, 3, 7, '3 to 7 assets');
  const objectiveStages = asStringArray(slotsRaw.objectiveStages, 'slots.objectiveStages', errors, 1, 6, '1 to 6 assets');
  const tutorialHand = asString(slotsRaw.tutorialHand, 'slots.tutorialHand', errors);
  const hudPanel = asString(slotsRaw.hudPanel, 'slots.hudPanel', errors);
  const ctaButton = asString(slotsRaw.ctaButton, 'slots.ctaButton', errors);
  const logo = slotsRaw.logo === undefined ? undefined : asString(slotsRaw.logo, 'slots.logo', errors);
  const reward = slotsRaw.reward === undefined ? undefined : asString(slotsRaw.reward, 'slots.reward', errors);
  // V1.1 Б3 additive optional slot: absent = no blockers renderable; length >= 2 required when present.
  let blockerStages: string[] | undefined;
  if (slotsRaw.blockerStages !== undefined) {
    const rawBlockerStages = asArray(slotsRaw.blockerStages, 'slots.blockerStages', errors);
    if (rawBlockerStages !== undefined) {
      if (rawBlockerStages.length < 2) {
        errors.push('slots.blockerStages must include at least 2 stages when present');
      }
      blockerStages = rawBlockerStages
        .map((entry, index) => asString(entry, `slots.blockerStages[${String(index)}]`, errors))
        .filter((entry): entry is string => entry !== undefined);
    }
  }

  // V1.2 additive optional: intrinsic background texture size for true cover-fit math.
  // Absent = legacy stretch to vpW×vpH (back-compat).
  let backgroundSize: { readonly w: number; readonly h: number } | undefined;
  if (slotsRaw.backgroundSize !== undefined) {
    if (!isRecord(slotsRaw.backgroundSize)) {
      errors.push('slots.backgroundSize must be an object when present');
    } else {
      const bsW = asNumber(slotsRaw.backgroundSize.w, 'slots.backgroundSize.w', errors, 1);
      const bsH = asNumber(slotsRaw.backgroundSize.h, 'slots.backgroundSize.h', errors, 1);
      if (bsW !== undefined && bsH !== undefined) {
        backgroundSize = { w: bsW, h: bsH };
      }
    }
  }

  const fitRaw = raw.fit;
  if (!isRecord(fitRaw)) {
    errors.push('fit must be an object');
    return failSkin(errors);
  }
  const tileCellFill = asNumber(fitRaw.tileCellFill, 'fit.tileCellFill', errors, 0.55, 0.98);
  const tilePaddingPx = asInteger(fitRaw.tilePaddingPx, 'fit.tilePaddingPx', errors, 2, 24);
  const boardInsetPx = asInteger(fitRaw.boardInsetPx, 'fit.boardInsetPx', errors, 0, 48);
  const anchor = isPoint(fitRaw.anchor, 'fit.anchor', errors);
  if (anchor !== undefined) {
    if (anchor.x < 0 || anchor.x > 1) errors.push('fit.anchor.x must be in [0,1]');
    if (anchor.y < 0 || anchor.y > 1) errors.push('fit.anchor.y must be in [0,1]');
  }

  // V1.1 additive optional: presentation overrides. Absence = exact current renderer behavior.
  // @intent Parse skin.presentation block; missing = undefined (back-compat; existing skins unaffected).
  // @flow raw.presentation -> each field validated independently; errors are non-fatal (field ignored on failure).
  // @stale Keep field list in sync with Match3SkinPresentationV1 interface and renderer presentation block.
  let presentation: Match3SkinPresentationV1 | undefined;
  if (raw.presentation !== undefined) {
    if (!isRecord(raw.presentation)) {
      errors.push('presentation must be an object when present');
    } else {
      const presRaw = raw.presentation;

      const presBoardPanel = presRaw.boardPanel === undefined
        ? undefined
        : asEnum<'plain' | 'none'>(presRaw.boardPanel, 'presentation.boardPanel', ['plain', 'none'], errors);

      let presCellChecker: Match3SkinCellCheckerModeV1 | undefined;
      if (presRaw.cellChecker !== undefined) {
        if (presRaw.cellChecker === 'none') {
          // 'none' sentinel: skip all per-cell socket/checker drawing.
          presCellChecker = 'none';
        } else if (!isRecord(presRaw.cellChecker)) {
          errors.push('presentation.cellChecker must be an object or "none" when present');
        } else {
          const ccRaw = presRaw.cellChecker;
          const ccColorA = asString(ccRaw.colorA, 'presentation.cellChecker.colorA', errors);
          const ccColorB = asString(ccRaw.colorB, 'presentation.cellChecker.colorB', errors);
          const ccAlpha = asNumber(ccRaw.alpha, 'presentation.cellChecker.alpha', errors, 0, 1);
          if (ccColorA !== undefined && !isHexColor(ccColorA)) errors.push('presentation.cellChecker.colorA must be a #RRGGBB color');
          if (ccColorB !== undefined && !isHexColor(ccColorB)) errors.push('presentation.cellChecker.colorB must be a #RRGGBB color');
          if (ccColorA !== undefined && ccColorB !== undefined && ccAlpha !== undefined) {
            presCellChecker = { colorA: ccColorA, colorB: ccColorB, alpha: ccAlpha };
          }
        }
      }

      const presHudVisible = presRaw.hudVisible === undefined
        ? undefined
        : typeof presRaw.hudVisible === 'boolean'
          ? presRaw.hudVisible
          : (errors.push('presentation.hudVisible must be a boolean when present'), undefined);

      const presTileShadow = presRaw.tileShadow === undefined
        ? undefined
        : typeof presRaw.tileShadow === 'boolean'
          ? presRaw.tileShadow
          : (errors.push('presentation.tileShadow must be a boolean when present'), undefined);

      let presBanner: Match3SkinBannerV1 | undefined;
      if (presRaw.banner !== undefined) {
        if (!isRecord(presRaw.banner)) {
          errors.push('presentation.banner must be an object when present');
        } else {
          const banRaw = presRaw.banner;
          const banAssetUrl = asString(banRaw.assetUrl, 'presentation.banner.assetUrl', errors);
          const banHeightPx = asInteger(banRaw.heightPx, 'presentation.banner.heightPx', errors, 20, 400);
          if (banAssetUrl !== undefined && banHeightPx !== undefined) {
            presBanner = { assetUrl: banAssetUrl, heightPx: banHeightPx };
          }
        }
      }

      let presAmbientSprites: readonly Match3SkinAmbientSpriteV1[] | undefined;
      if (presRaw.ambientSprites !== undefined) {
        const ambRaw = asArray(presRaw.ambientSprites, 'presentation.ambientSprites', errors);
        if (ambRaw !== undefined) {
          const parsed: Match3SkinAmbientSpriteV1[] = [];
          for (let i = 0; i < ambRaw.length; i += 1) {
            const entry = ambRaw[i];
            if (!isRecord(entry)) {
              errors.push(`presentation.ambientSprites[${String(i)}] must be an object`);
              continue;
            }
            const ambAssetUrl = asString(entry.assetUrl, `presentation.ambientSprites[${String(i)}].assetUrl`, errors);
            const ambX = asNumber(entry.x, `presentation.ambientSprites[${String(i)}].x`, errors, -9999, 9999);
            const ambY = asNumber(entry.y, `presentation.ambientSprites[${String(i)}].y`, errors, -9999, 9999);
            const ambSize = asNumber(entry.size, `presentation.ambientSprites[${String(i)}].size`, errors, 1, 2000);
            if (ambAssetUrl !== undefined && ambX !== undefined && ambY !== undefined && ambSize !== undefined) {
              parsed.push({ assetUrl: ambAssetUrl, x: ambX, y: ambY, size: ambSize });
            }
          }
          presAmbientSprites = parsed;
        }
      }

      presentation = {
        ...(presBoardPanel === undefined ? {} : { boardPanel: presBoardPanel }),
        ...(presCellChecker === undefined ? {} : { cellChecker: presCellChecker }),
        ...(presHudVisible === undefined ? {} : { hudVisible: presHudVisible }),
        ...(presBanner === undefined ? {} : { banner: presBanner }),
        ...(presAmbientSprites === undefined ? {} : { ambientSprites: presAmbientSprites }),
        ...(presTileShadow === undefined ? {} : { tileShadow: presTileShadow }),
      };
    }
  }

  if (
    errors.length > 0 ||
    version !== 'v1' ||
    id === undefined ||
    styleName === undefined ||
    palette === undefined ||
    lighting === undefined ||
    perspective === undefined ||
    tileShape === undefined ||
    backgroundPolicy === undefined ||
    background === undefined ||
    boardPanel === undefined ||
    tileTypes === undefined ||
    objectiveStages === undefined ||
    tutorialHand === undefined ||
    hudPanel === undefined ||
    ctaButton === undefined ||
    tileCellFill === undefined ||
    tilePaddingPx === undefined ||
    boardInsetPx === undefined ||
    anchor === undefined
  ) {
    return failSkin(errors);
  }

  return {
    ok: true,
    value: {
      version: 'v1',
      id,
      ...(sourceReferenceId === undefined ? {} : { sourceReferenceId }),
      styleBible: {
        styleName,
        palette,
        lighting,
        perspective,
        tileShape,
        backgroundPolicy,
      },
      slots: {
        background,
        boardPanel,
        tileTypes,
        objectiveStages,
        tutorialHand,
        hudPanel,
        ctaButton,
        ...(logo === undefined ? {} : { logo }),
        ...(reward === undefined ? {} : { reward }),
        ...(blockerStages === undefined ? {} : { blockerStages }),
        ...(backgroundSize === undefined ? {} : { backgroundSize }),
      },
      fit: {
        tileCellFill,
        tilePaddingPx,
        boardInsetPx,
        anchor,
      },
      ...(presentation === undefined ? {} : { presentation }),
    },
  };
}

export function validateMatch3NativeRuntimePayloadV1(payload: unknown): Match3NativeRuntimePayloadValidationResult {
  const errors: string[] = [];
  if (!isRecord(payload)) return failPayload(['nativeMatch3 must be an object']);

  const version = asString(payload.version, 'version', errors);
  if (version !== 'v1') errors.push(`version must equal 'v1'`);

  const levelResult = validateMatch3LevelV1(payload.level);
  const skinResult = validateMatch3SkinPackV1(payload.skin);

  if (!levelResult.ok) {
    errors.push(...levelResult.errors.map((error) => `level.${error}`));
  }
  if (!skinResult.ok) {
    errors.push(...skinResult.errors.map((error) => `skin.${error}`));
  }

  if (errors.length > 0 || !levelResult.ok || !skinResult.ok) return failPayload(errors);

  return {
    ok: true,
    value: {
      version: 'v1',
      level: levelResult.value,
      skin: skinResult.value,
    },
  };
}

function parseMatch3Tutorial(
  value: unknown,
  expectedSize: number,
  errors: string[],
): Match3TutorialV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push('tutorial must be an object');
    return undefined;
  }
  const hand = value.hand === undefined ? undefined : asBoolean(value.hand, 'tutorial.hand', errors);
  const copy = value.copy === undefined ? undefined : asString(value.copy, 'tutorial.copy', errors);
  const firstMoveOnly =
    value.firstMoveOnly === undefined ? undefined : asBoolean(value.firstMoveOnly, 'tutorial.firstMoveOnly', errors);
  const masksRaw = value.masks === undefined ? undefined : asArray(value.masks, 'tutorial.masks', errors);
  const masks: number[][] = [];
  if (masksRaw !== undefined) {
    masksRaw.forEach((mask, maskIndex) => {
      const path = `tutorial.masks[${String(maskIndex)}]`;
      const maskRaw = asArray(mask, path, errors);
      if (maskRaw === undefined) return;
      if (maskRaw.length !== expectedSize) {
        errors.push(`${path} length ${String(maskRaw.length)} must equal rows*cols ${String(expectedSize)}`);
      }
      const parsed = maskRaw
        .map((entry, entryIndex) => asInteger(entry, `${path}[${String(entryIndex)}]`, errors, 0, 99))
        .filter((entry): entry is number => entry !== undefined);
      masks.push(parsed);
    });
  }
  return {
    ...(masksRaw === undefined ? {} : { masks }),
    ...(hand === undefined ? {} : { hand }),
    ...(copy === undefined ? {} : { copy }),
    ...(firstMoveOnly === undefined ? {} : { firstMoveOnly }),
  };
}

function parseMatch3Pacing(value: unknown, errors: string[]): Match3PacingV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push('pacing must be an object');
    return undefined;
  }
  const swapMs = value.swapMs === undefined ? undefined : asInteger(value.swapMs, 'pacing.swapMs', errors, 1, 30000);
  const clearMs = value.clearMs === undefined ? undefined : asInteger(value.clearMs, 'pacing.clearMs', errors, 1, 30000);
  const gravityMs =
    value.gravityMs === undefined ? undefined : asInteger(value.gravityMs, 'pacing.gravityMs', errors, 1, 30000);
  const refillDelayMs =
    value.refillDelayMs === undefined
      ? undefined
      : asInteger(value.refillDelayMs, 'pacing.refillDelayMs', errors, 1, 30000);
  const objectiveFlyMs =
    value.objectiveFlyMs === undefined
      ? undefined
      : asInteger(value.objectiveFlyMs, 'pacing.objectiveFlyMs', errors, 1, 30000);
  return {
    ...(swapMs === undefined ? {} : { swapMs }),
    ...(clearMs === undefined ? {} : { clearMs }),
    ...(gravityMs === undefined ? {} : { gravityMs }),
    ...(refillDelayMs === undefined ? {} : { refillDelayMs }),
    ...(objectiveFlyMs === undefined ? {} : { objectiveFlyMs }),
  };
}

function parseMatch3Pvp(value: unknown, errors: string[]): Match3PvpConfigV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push('pvp must be an object');
    return undefined;
  }
  const mode = value.mode === undefined ? undefined : asEnum(value.mode, 'pvp.mode', ['pvp', 'pve'] as const, errors);
  const firstTurn =
    value.firstTurn === undefined
      ? undefined
      : asEnum(value.firstTurn, 'pvp.firstTurn', ['player', 'opponent'] as const, errors);
  const preplayedMove =
    value.preplayedMove === undefined ? undefined : asBoolean(value.preplayedMove, 'pvp.preplayedMove', errors);
  const roundLength =
    value.roundLength === undefined ? undefined : asInteger(value.roundLength, 'pvp.roundLength', errors, 1, 20);
  const playerMoveCount =
    value.playerMoveCount === undefined
      ? undefined
      : asInteger(value.playerMoveCount, 'pvp.playerMoveCount', errors, 1, 100);
  const opponentMoveCount =
    value.opponentMoveCount === undefined
      ? undefined
      : asInteger(value.opponentMoveCount, 'pvp.opponentMoveCount', errors, 1, 100);
  const difficulty =
    value.difficulty === undefined ? undefined : asInteger(value.difficulty, 'pvp.difficulty', errors, 0, 10);
  const playerTarget =
    value.playerTarget === undefined ? undefined : asInteger(value.playerTarget, 'pvp.playerTarget', errors, 1);
  const opponentTarget =
    value.opponentTarget === undefined ? undefined : asInteger(value.opponentTarget, 'pvp.opponentTarget', errors, 1);
  const aiThinkMs =
    value.aiThinkMs === undefined ? undefined : asInteger(value.aiThinkMs, 'pvp.aiThinkMs', errors, 0, 30000);
  const aiFollowUpMs =
    value.aiFollowUpMs === undefined ? undefined : asInteger(value.aiFollowUpMs, 'pvp.aiFollowUpMs', errors, 0, 30000);
  const playerBooster =
    value.playerBooster === undefined ? undefined : asString(value.playerBooster, 'pvp.playerBooster', errors);
  const opponentBooster =
    value.opponentBooster === undefined ? undefined : asString(value.opponentBooster, 'pvp.opponentBooster', errors);
  return {
    ...(mode === undefined ? {} : { mode }),
    ...(firstTurn === undefined ? {} : { firstTurn }),
    ...(preplayedMove === undefined ? {} : { preplayedMove }),
    ...(roundLength === undefined ? {} : { roundLength }),
    ...(playerMoveCount === undefined ? {} : { playerMoveCount }),
    ...(opponentMoveCount === undefined ? {} : { opponentMoveCount }),
    ...(difficulty === undefined ? {} : { difficulty }),
    ...(playerTarget === undefined ? {} : { playerTarget }),
    ...(opponentTarget === undefined ? {} : { opponentTarget }),
    ...(aiThinkMs === undefined ? {} : { aiThinkMs }),
    ...(aiFollowUpMs === undefined ? {} : { aiFollowUpMs }),
    ...(playerBooster === undefined ? {} : { playerBooster }),
    ...(opponentBooster === undefined ? {} : { opponentBooster }),
  };
}

function parseMatch3Specials(value: unknown, errors: string[]): readonly Match3SpecialConfigV1[] | undefined {
  if (value === undefined) return undefined;
  const raw = asArray(value, 'specials', errors);
  if (raw === undefined) return undefined;
  return raw
    .map((entry, index): Match3SpecialConfigV1 | undefined => {
      const path = `specials[${String(index)}]`;
      if (!isRecord(entry)) {
        errors.push(`${path} must be an object`);
        return undefined;
      }
      const id = asString(entry.id, `${path}.id`, errors);
      const kind = asString(entry.kind, `${path}.kind`, errors);
      const spawn =
        entry.spawn === undefined
          ? undefined
          : asEnum(entry.spawn, `${path}.spawn`, ['none', 'scripted', 'firstMove', 'cascade', 'random'] as const, errors);
      const effect = entry.effect === undefined ? undefined : asString(entry.effect, `${path}.effect`, errors);
      const guaranteeOnFirstMove =
        entry.guaranteeOnFirstMove === undefined
          ? undefined
          : asBoolean(entry.guaranteeOnFirstMove, `${path}.guaranteeOnFirstMove`, errors);
      // V1.1 Б1/E3: optional pre-placed coordinate — parsed permissively (no board bounds check here; engine validates).
      const at =
        entry.at === undefined ? undefined : asCoordinate(entry.at, `${path}.at`, 0, 0, errors);
      if (id === undefined || kind === undefined) return undefined;
      return {
        id,
        kind,
        ...(spawn === undefined ? {} : { spawn }),
        ...(effect === undefined ? {} : { effect }),
        ...(guaranteeOnFirstMove === undefined ? {} : { guaranteeOnFirstMove }),
        ...(at === undefined ? {} : { at }),
      };
    })
    .filter((entry): entry is Match3SpecialConfigV1 => entry !== undefined);
}

function parseMatch3Feedback(value: unknown, errors: string[]): Match3FeedbackV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push('feedback must be an object');
    return undefined;
  }
  let collectFlight: Match3CollectFlightFeedbackV1 | undefined;
  if (value.collectFlight !== undefined) {
    if (!isRecord(value.collectFlight)) {
      errors.push('feedback.collectFlight must be an object');
    } else {
      const raw = value.collectFlight;
      const enabled =
        raw.enabled === undefined ? undefined : asBoolean(raw.enabled, 'feedback.collectFlight.enabled', errors);
      const sourceRole =
        raw.sourceRole === undefined ? undefined : asString(raw.sourceRole, 'feedback.collectFlight.sourceRole', errors);
      const target = asEnum(
        raw.target,
        'feedback.collectFlight.target',
        ['objectiveHud', 'scoreHud', 'progressHud'] as const,
        errors,
      );
      const durationMs = asInteger(raw.durationMs, 'feedback.collectFlight.durationMs', errors, 1, 30000);
      const trail = raw.trail === undefined ? undefined : asBoolean(raw.trail, 'feedback.collectFlight.trail', errors);
      if (target !== undefined && durationMs !== undefined) {
        collectFlight = {
          ...(enabled === undefined ? {} : { enabled }),
          ...(sourceRole === undefined ? {} : { sourceRole }),
          target,
          durationMs,
          ...(trail === undefined ? {} : { trail }),
        };
      }
    }
  }
  return {
    ...(collectFlight === undefined ? {} : { collectFlight }),
  };
}

export function validateMatch3LevelV1(level: unknown): Match3LevelValidationResult {
  const errors: string[] = [];
  const dedupe = new Set<string>();
  const dedupedPush = (msg: string): void => {
    pushError(errors, msg, dedupe);
  };

  if (!isRecord(level)) return fail([`level must be an object`]);
  const raw = level;

  const version = asString(raw.version, 'version', errors);
  if (version !== 'v1') {
    errors.push(`version must equal 'v1'`);
  }
  const family = asString(raw.family, 'family', errors);
  if (family !== 'match3') {
    errors.push(`family must equal 'match3'`);
  }
  const topologyId = asString(raw.topologyId, 'topologyId', errors);
  const topologyHash = asString(raw.topologyHash, 'topologyHash', errors);

  const boardRaw = raw.board;
  if (!isRecord(boardRaw)) {
    errors.push('board must be an object');
    return fail(errors);
  }
  const rows = asInteger(boardRaw.rows, 'board.rows', errors, 1);
  const cols = asInteger(boardRaw.cols, 'board.cols', errors, 1);
  const cellsRaw = asArray(boardRaw.cells, 'board.cells', errors);
  const expectedSize = rows !== undefined && cols !== undefined ? rows * cols : 0;

  const cellTiles: Match3BoardCellV1[] = [];
  if (cellsRaw !== undefined) {
    if (expectedSize > 0 && cellsRaw.length !== expectedSize) {
      dedupedPush(`board.cells length ${String(cellsRaw.length)} must equal rows*cols ${String(expectedSize)}`);
    }
    for (let i = 0; i < cellsRaw.length; i += 1) {
      const value = cellsRaw[i];
      if (value === null) {
        cellTiles.push(null);
        continue;
      }
      const tileId = asString(value, `board.cells[${String(i)}]`, errors);
      if (tileId !== undefined) {
        cellTiles.push(tileId);
      } else {
        cellTiles.push(null);
      }
    }
  }

  const enabledMask =
    boardRaw.enabledMask === undefined ? undefined : asArray(boardRaw.enabledMask, 'board.enabledMask', errors);
  if (enabledMask !== undefined) {
    if (expectedSize > 0 && enabledMask.length !== expectedSize) {
      dedupedPush(`board.enabledMask length must equal rows*cols ${String(expectedSize)}`);
    }
    for (let i = 0; i < enabledMask.length; i += 1) {
      if (typeof enabledMask[i] !== 'boolean') {
        dedupedPush(`board.enabledMask[${String(i)}] must be boolean`);
      }
    }
  }

  const blockersRaw = boardRaw.blockers === undefined ? undefined : asArray(boardRaw.blockers, 'board.blockers', errors);
  const staticCellsRaw = boardRaw.staticCells === undefined ? undefined : asArray(boardRaw.staticCells, 'board.staticCells', errors);
  const collectibleUnderlaysRaw = boardRaw.collectibleUnderlays === undefined ? undefined : asArray(boardRaw.collectibleUnderlays, 'board.collectibleUnderlays', errors);

  const blockers: Match3BlockerV1[] = [];
  const staticCells: Match3StaticCellV1[] = [];
  const coordinates = new Map<number, string>();

  if (rows !== undefined && cols !== undefined) {
    if (blockersRaw !== undefined) {
      blockersRaw.forEach((entry, idx) => {
        const path = `board.blockers[${String(idx)}]`;
        if (!isRecord(entry)) {
          dedupedPush(`${path} must be an object`);
          return;
        }
        const id = asString(entry.id, `${path}.id`, errors);
        const type = asString(entry.kind, `${path}.kind`, errors) as Match3BlockerV1['kind'] | undefined;
        if (type !== undefined && !['wall', 'obstacle', 'void'].includes(type)) {
          dedupedPush(`${path}.kind must be wall|obstacle|void`);
        }
        const swappable = asBoolean(entry.swappable, `${path}.swappable`, errors);
        const fillsCell = entry.fillsCell === undefined ? undefined : asBoolean(entry.fillsCell, `${path}.fillsCell`, errors);
        const fillTileId =
          entry.fillTileId === undefined ? undefined : asString(entry.fillTileId, `${path}.fillTileId`, errors);
        const at = asCoordinate(entry.at, `${path}.at`, rows, cols, errors);
        if (at === undefined) return;
        const i = indexOfCell(rows, cols, at.row, at.col);
        const existing = coordinates.get(i);
        if (existing !== undefined) {
          dedupedPush(`duplicate board occupancy at ${String(at.row)}x${String(at.col)} from ${existing}`);
        } else {
          coordinates.set(i, path);
        }
        if (id === undefined || type === undefined || swappable === undefined) return;
        if (fillsCell === true && fillTileId === undefined) {
          dedupedPush(`${path}.fillTileId is required when fillsCell is true`);
        }
        if (fillsCell === true && swappable) {
          dedupedPush(`${path}.swappable must be false when fillsCell is true`);
        }
        blockers.push({
          id,
          at,
          kind: type,
          swappable,
          ...(fillsCell === undefined ? {} : { fillsCell }),
          ...(fillTileId === undefined ? {} : { fillTileId }),
        });
      });
    }

    if (staticCellsRaw !== undefined) {
      staticCellsRaw.forEach((entry, idx) => {
        const path = `board.staticCells[${String(idx)}]`;
        if (!isRecord(entry)) {
          dedupedPush(`${path} must be an object`);
          return;
        }
        const at = asCoordinate(entry.at, `${path}.at`, rows, cols, errors);
        const tileId = asString(entry.tileId, `${path}.tileId`, errors);
        const swappable = asBoolean(entry.swappable, `${path}.swappable`, errors);
        const role = entry.role === undefined ? undefined : asString(entry.role, `${path}.role`, errors);
        const stage = entry.stage === undefined ? undefined : asInteger(entry.stage, `${path}.stage`, errors, 0, 12);
        const maxStage = entry.maxStage === undefined ? undefined : asInteger(entry.maxStage, `${path}.maxStage`, errors, 1, 12);
        const clearRule =
          entry.clearRule === undefined
            ? undefined
            : (asString(entry.clearRule, `${path}.clearRule`, errors) as Match3StaticCellClearRuleV1 | undefined);
        if (clearRule !== undefined && !['adjacent-match', 'matched-cell', 'scripted'].includes(clearRule)) {
          dedupedPush(`${path}.clearRule must be adjacent-match|matched-cell|scripted`);
        }
        if (at === undefined || tileId === undefined || swappable === undefined) return;
        if (swappable) {
          dedupedPush(`${path}.swappable must be false for static cells`);
        }
        const i = indexOfCell(rows, cols, at.row, at.col);
        const existing = coordinates.get(i);
        if (existing !== undefined) {
          dedupedPush(`duplicate board occupancy at ${String(at.row)}x${String(at.col)} from ${existing}`);
        } else {
          coordinates.set(i, path);
        }
        staticCells.push({
          at,
          tileId,
          swappable: false,
          ...(role === undefined ? {} : { role }),
          ...(stage === undefined ? {} : { stage }),
          ...(maxStage === undefined ? {} : { maxStage }),
          ...(clearRule === undefined ? {} : { clearRule }),
        });
      });
    }
  }

  // Parse collectibleUnderlays (V1.1 additive — absent = no underlay mechanic).
  // Validator: each `at` must be an enabled non-hole cell, NOT also a staticCell or blocker; role non-empty.
  const collectibleUnderlays: Match3CollectibleUnderlayV1[] = [];
  if (rows !== undefined && cols !== undefined && collectibleUnderlaysRaw !== undefined) {
    const staticCellIndexSet = new Set(staticCells.map((sc) => indexOfCell(rows, cols, sc.at.row, sc.at.col)));
    const blockerIndexSet = new Set(blockers.map((b) => indexOfCell(rows, cols, b.at.row, b.at.col)));
    collectibleUnderlaysRaw.forEach((entry, idx) => {
      const path = `board.collectibleUnderlays[${String(idx)}]`;
      if (!isRecord(entry)) {
        dedupedPush(`${path} must be an object`);
        return;
      }
      const at = asCoordinate(entry.at, `${path}.at`, rows, cols, errors);
      const role = asString(entry.role as unknown, `${path}.role`, errors);
      if (at === undefined || role === undefined) return;
      const i = indexOfCell(rows, cols, at.row, at.col);
      const enabled = enabledMask === undefined ? true : enabledMask[i] !== false;
      if (!enabled) {
        dedupedPush(`${path}.at must be an enabled cell (not disabled by enabledMask)`);
        return;
      }
      if (staticCellIndexSet.has(i)) {
        dedupedPush(`${path}.at cannot overlap a staticCell`);
        return;
      }
      if (blockerIndexSet.has(i)) {
        dedupedPush(`${path}.at cannot overlap a blocker`);
        return;
      }
      collectibleUnderlays.push({ at, role });
    });
  }

  const tilesRaw = asArray(raw.tiles, 'tiles', errors);
  const tiles: Match3TileDefinitionV1[] = [];
  if (tilesRaw !== undefined) {
    const tileIds = new Set<string>();
    tilesRaw.forEach((tile, idx) => {
      const path = `tiles[${String(idx)}]`;
      if (!isRecord(tile)) {
        dedupedPush(`${path} must be an object`);
        return;
      }
      const id = asString(tile.id, `${path}.id`, errors);
      const assetUrl = asString(tile.assetUrl, `${path}.assetUrl`, errors);
      const swappable = tile.swappable === undefined ? undefined : asBoolean(tile.swappable, `${path}.swappable`, errors);
      const assetFit = asAssetFit(tile.assetFit, `${path}.assetFit`, errors);
      if (id === undefined || assetUrl === undefined || (tile.swappable !== undefined && swappable === undefined)) return;
      if (tileIds.has(id)) dedupedPush(`tiles[${String(idx)}].id must be unique`);
      tileIds.add(id);
      tiles.push({
        id,
        assetUrl,
        ...(assetFit === undefined ? {} : { assetFit }),
        ...(swappable === undefined ? {} : { swappable }),
      });
    });
  }

  const tileIds = new Set(tiles.map((tile) => tile.id));
  const markTileUsage = (tileId: Match3BoardCellV1 | undefined, path: string): void => {
    if (tileId === null || tileId === undefined) return;
    if (!tileIds.has(tileId)) {
      dedupedPush(`${path} references unknown tile id '${tileId}'`);
    }
  };
  if (cellTiles.length > 0) {
    for (let i = 0; i < cellTiles.length; i += 1) {
      markTileUsage(cellTiles[i], `board.cells[${String(i)}]`);
    }
  }

  if (rows !== undefined && cols !== undefined) {
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const i = indexOfCell(rows, cols, r, c);
        const rawCell = cellTiles[i];
        const block = blockers.find((item) => indexOfCell(rows, cols, item.at.row, item.at.col) === i);
        const staticCell = staticCells.find((item) => indexOfCell(rows, cols, item.at.row, item.at.col) === i);
        const enabled = enabledMask === undefined ? true : enabledMask[i] !== false;
        if (!enabled && rawCell !== undefined && rawCell !== null) {
          dedupedPush(`board.cells[${String(i)}] should be null when disabled`);
        }
        const blockerDisables = block !== undefined && !block.swappable && block.fillsCell !== true;
        const blockerFills = staticCell !== undefined || block?.fillsCell === true;
        if (enabled && !blockerDisables && !blockerFills && (rawCell === null || rawCell === undefined)) {
          dedupedPush(`enabled board.cells[${String(i)}] must contain a tile id`);
        }
        if (blockerFills) {
          if (block?.fillsCell === true && block.fillTileId !== undefined) {
            markTileUsage(block.fillTileId, `board.blockers[?]`);
          }
          if (staticCell !== undefined) markTileUsage(staticCell.tileId, `board.staticCells[?]`);
        }
      }
    }
  }

  if (staticCells.length > 0) {
    staticCells.forEach((item, idx) => {
      if (!tileIds.has(item.tileId)) {
        dedupedPush(`board.staticCells[${String(idx)}].tileId references unknown tile id '${item.tileId}'`);
      }
    });
  }
  if (blockers.length > 0) {
    blockers.forEach((item, idx) => {
      if (item.fillsCell === true && item.fillTileId !== undefined && !tileIds.has(item.fillTileId)) {
        dedupedPush(`board.blockers[${String(idx)}].fillTileId references unknown tile id '${item.fillTileId}'`);
      }
    });
  }

  const layoutRaw = raw.layout;
  if (!isRecord(layoutRaw)) {
    errors.push('layout must be an object');
    return fail(errors);
  }
  const viewportWidthPx = asInteger(layoutRaw.viewportWidthPx, 'layout.viewportWidthPx', errors, 1);
  const viewportHeightPx = asInteger(layoutRaw.viewportHeightPx, 'layout.viewportHeightPx', errors, 1);
  const sizingRaw = layoutRaw.sizing;
  if (!isRecord(sizingRaw)) {
    errors.push('layout.sizing must be an object');
    return fail(errors);
  }
  const boardRect = isRect(sizingRaw.boardRect, 'layout.sizing.boardRect', errors);
  const tileSizePx = asInteger(sizingRaw.tileSizePx, 'layout.sizing.tileSizePx', errors, 1);
  const tileGapPx = asInteger(sizingRaw.tileGapPx, 'layout.sizing.tileGapPx', errors, 0);

  if (
    rows !== undefined &&
    cols !== undefined &&
    boardRect !== undefined &&
    tileSizePx !== undefined &&
    tileGapPx !== undefined
  ) {
    const requiredWidth = cols * tileSizePx + Math.max(0, cols - 1) * tileGapPx;
    const requiredHeight = rows * tileSizePx + Math.max(0, rows - 1) * tileGapPx;
    if (requiredWidth > boardRect.width) {
      dedupedPush('layout.sizing.boardRect.width is too small for cols*tileSizePx + gaps');
    }
    if (requiredHeight > boardRect.height) {
      dedupedPush('layout.sizing.boardRect.height is too small for rows*tileSizePx + gaps');
    }
  }

  const objectiveRaw = raw.objective;
  if (!isRecord(objectiveRaw)) {
    errors.push('objective must be an object');
    return fail(errors);
  }
  const objectiveType = asString(objectiveRaw.type, 'objective.type', errors) as Match3ObjectiveTypeV1 | undefined;
  if (
    objectiveType !== undefined &&
    !['collectN', 'scoreTarget', 'pvpRace', 'grow'].includes(objectiveType)
  ) {
    dedupedPush('objective.type must be collectN|scoreTarget|pvpRace|grow');
  }
  const objectiveTarget = asInteger(objectiveRaw.target, 'objective.target', errors, 1);
  const objectiveRole =
    objectiveRaw.role === undefined ? undefined : asString(objectiveRaw.role, 'objective.role', errors);
  if (objectiveType === undefined || objectiveTarget === undefined) return fail(errors);
  const objective: Match3ObjectiveV1 = {
    type: objectiveType,
    target: objectiveTarget,
    ...(objectiveRole === undefined ? {} : { role: objectiveRole }),
  };

  const limitRaw = raw.limit;
  if (!isRecord(limitRaw)) {
    errors.push('limit must be an object');
    return fail(errors);
  }
  const kind = asString(limitRaw.kind, 'limit.kind', errors);
  if (kind !== undefined && !['moves', 'timer', 'turns', 'none'].includes(kind)) {
    dedupedPush('limit.kind must be moves|timer|turns|none');
  }
  let limit: Match3LimitV1 | undefined;
  if (kind === 'moves' || kind === 'turns') {
    const value = asInteger(limitRaw.value, `limit.${kind}`, errors, 1);
    if (value === undefined) return fail(errors);
    limit = { kind, value };
  } else if (kind === 'timer') {
    const seconds = asInteger(limitRaw.seconds, 'limit.seconds', errors, 1);
    if (seconds === undefined) return fail(errors);
    limit = { kind, seconds };
  } else if (kind === 'none') {
    limit = { kind };
  }

  let script: Match3LevelScriptV1 | undefined;
  const scriptRaw = raw.script;
  if (scriptRaw !== undefined) {
    if (!isRecord(scriptRaw)) {
      errors.push('script must be an object');
    } else if (rows !== undefined && cols !== undefined) {
      const firstMove = asScriptMove(scriptRaw.firstMove, 'script.firstMove', rows, cols, errors);
      const winningMovesRaw = asArray(scriptRaw.winningMoves, 'script.winningMoves', errors);
      const winningMoves: Match3ScriptMoveV1[] = [];
      if (winningMovesRaw !== undefined) {
        winningMovesRaw.forEach((move, index) => {
          const parsed = asScriptMove(move, `script.winningMoves[${String(index)}]`, rows, cols, errors);
          if (parsed !== undefined) winningMoves.push(parsed);
        });
      }
      const allowFreePlayAfterScript =
        scriptRaw.allowFreePlayAfterScript === undefined
          ? undefined
          : asBoolean(scriptRaw.allowFreePlayAfterScript, 'script.allowFreePlayAfterScript', errors);
      if (firstMove !== undefined && winningMovesRaw !== undefined && (scriptRaw.allowFreePlayAfterScript === undefined || allowFreePlayAfterScript !== undefined)) {
        script = {
          firstMove,
          winningMoves,
          ...(allowFreePlayAfterScript === undefined ? {} : { allowFreePlayAfterScript }),
        };
      }
    }
  }

  const telemetryRaw = raw.telemetry;
  let telemetry: Match3TelemetryV1 | undefined;
  if (telemetryRaw !== undefined) {
    if (!isRecord(telemetryRaw)) {
      errors.push('telemetry must be an object');
    } else {
      const enabled = telemetryRaw.enabled === undefined ? undefined : asBoolean(telemetryRaw.enabled, 'telemetry.enabled', errors);
      const requiredEvents =
        telemetryRaw.requiredEvents === undefined
          ? undefined
          : asArray(telemetryRaw.requiredEvents, 'telemetry.requiredEvents', errors);
      const requiredEventNames = requiredEvents?.filter((value): value is string => {
        if (typeof value !== 'string' || value.length === 0) {
          dedupedPush('telemetry.requiredEvents entries must be non-empty strings');
          return false;
        }
        return true;
      });

      const probesRaw = telemetryRaw.probes;
      const telemetryExpectations =
        probesRaw === undefined || !isRecord(probesRaw) || probesRaw.expectations === undefined
          ? undefined
        : ((): Match3ProbeExpectationsV1 | undefined => {
              if (!isRecord(probesRaw.expectations)) return undefined;
              const expectationRaw = probesRaw.expectations;
              const expectation: { boardTileCount?: boolean; objectiveRemaining?: boolean; ctaShown?: boolean; nextMoveHint?: boolean } =
                {};
              let hasExpectation = false;
              if (typeof expectationRaw.boardTileCount === 'boolean') {
                expectation.boardTileCount = expectationRaw.boardTileCount;
                hasExpectation = true;
              }
              if (typeof expectationRaw.objectiveRemaining === 'boolean') {
                expectation.objectiveRemaining = expectationRaw.objectiveRemaining;
                hasExpectation = true;
              }
              if (typeof expectationRaw.ctaShown === 'boolean') {
                expectation.ctaShown = expectationRaw.ctaShown;
                hasExpectation = true;
              }
              if (typeof expectationRaw.nextMoveHint === 'boolean') {
                expectation.nextMoveHint = expectationRaw.nextMoveHint;
                hasExpectation = true;
              }
              return hasExpectation ? expectation : undefined;
            })();

      if (probesRaw !== undefined) {
        if (!isRecord(probesRaw)) {
          dedupedPush('telemetry.probes must be an object');
        } else {
          const expectationRaw = probesRaw.expectations;
          if (expectationRaw !== undefined && !isRecord(expectationRaw)) {
            dedupedPush('telemetry.probes.expectations must be an object');
          } else if (isRecord(expectationRaw)) {
            if (
              expectationRaw.boardTileCount !== undefined &&
              typeof expectationRaw.boardTileCount !== 'boolean'
            ) {
              dedupedPush('telemetry.probes.expectations.boardTileCount must be boolean');
            }
            if (
              expectationRaw.objectiveRemaining !== undefined &&
              typeof expectationRaw.objectiveRemaining !== 'boolean'
            ) {
              dedupedPush('telemetry.probes.expectations.objectiveRemaining must be boolean');
            }
            if (expectationRaw.ctaShown !== undefined && typeof expectationRaw.ctaShown !== 'boolean') {
              dedupedPush('telemetry.probes.expectations.ctaShown must be boolean');
            }
            if (expectationRaw.nextMoveHint !== undefined && typeof expectationRaw.nextMoveHint !== 'boolean') {
              dedupedPush('telemetry.probes.expectations.nextMoveHint must be boolean');
            }
          }
        }
      }

      const probes: { readonly expectations?: Match3ProbeExpectationsV1 } | undefined =
        probesRaw === undefined || !isRecord(probesRaw) || telemetryExpectations === undefined
          ? undefined
          : { expectations: telemetryExpectations };

      telemetry = {
        ...(enabled === undefined ? {} : { enabled }),
        ...(requiredEventNames === undefined ? {} : { requiredEvents: requiredEventNames }),
        ...(probes === undefined ? {} : { probes }),
      };
    }
  }

  const runtimeSeed = raw.runtimeSeed === undefined ? undefined : asInteger(raw.runtimeSeed, 'runtimeSeed', errors, 0);
  const tutorial = parseMatch3Tutorial(raw.tutorial, expectedSize, errors);
  const pacing = parseMatch3Pacing(raw.pacing, errors);
  const pvp = parseMatch3Pvp(raw.pvp, errors);
  const specials = parseMatch3Specials(raw.specials, errors);
  const feedback = parseMatch3Feedback(raw.feedback, errors);

  if (errors.length > 0 || !version || !family || !topologyId || !topologyHash || expectedSize === 0 || cellsRaw === undefined || tilesRaw === undefined) {
    return fail(errors);
  }

  const normalizedEnabledMask: readonly boolean[] =
    enabledMask === undefined ? new Array(expectedSize).fill(true) : enabledMask;

  const expectedBoardRows = rows;
  const expectedBoardCols = cols;
  const expectedViewportWidthPx = viewportWidthPx;
  const expectedViewportHeightPx = viewportHeightPx;
  const expectedBoardRect = boardRect;
  const expectedTileSizePx = tileSizePx;
  const expectedTileGapPx = tileGapPx;

  if (
    expectedBoardRows === undefined ||
    expectedBoardCols === undefined ||
    expectedViewportWidthPx === undefined ||
    expectedViewportHeightPx === undefined ||
    expectedBoardRect === undefined ||
    expectedTileSizePx === undefined ||
    expectedTileGapPx === undefined ||
    limit === undefined
  ) {
    return fail(errors);
  }

  if (version !== 'v1' || family !== 'match3') {
    return fail(errors);
  }

  const value: Match3LevelV1 = {
    version: 'v1',
    family: 'match3',
    topologyId,
    topologyHash,
    ...(runtimeSeed === undefined ? {} : { runtimeSeed }),
    board: {
      rows: expectedBoardRows,
      cols: expectedBoardCols,
      cells: cloneBoardCells(cellTiles),
      enabledMask: [...normalizedEnabledMask],
      blockers,
      staticCells,
      ...(collectibleUnderlays.length > 0 ? { collectibleUnderlays } : {}),
    },
    tiles,
    layout: {
      viewportWidthPx: expectedViewportWidthPx,
      viewportHeightPx: expectedViewportHeightPx,
      sizing: {
        boardRect: expectedBoardRect,
        tileSizePx: expectedTileSizePx,
        tileGapPx: expectedTileGapPx,
      },
    },
    objective,
    limit,
    ...(script === undefined ? {} : { script }),
    ...(telemetry === undefined ? {} : { telemetry }),
    ...(tutorial === undefined ? {} : { tutorial }),
    ...(pacing === undefined ? {} : { pacing }),
    ...(pvp === undefined ? {} : { pvp }),
    ...(specials === undefined ? {} : { specials }),
    ...(feedback === undefined ? {} : { feedback }),
  };

  return { ok: true, value };
}

function normalizeProbeBoard(value: unknown, rows: number, cols: number, errors: string[]): Match3ProbeBoardCellV1[] {
  const raw = asArray(value, 'board', errors);
  if (!raw) return [];
  const flattened = raw.every(Array.isArray) ? raw.flat() : raw;
  const expectedSize = rows * cols;
  if (flattened.length !== expectedSize) {
    errors.push(`board length ${String(flattened.length)} must equal rows*cols ${String(expectedSize)}`);
  }
  return flattened.map((cell, index): Match3ProbeBoardCellV1 => {
    if (cell === null) return null;
    if (typeof cell === 'string' && cell.length > 0) return cell;
    if (typeof cell === 'number' && Number.isInteger(cell)) return cell;
    errors.push(`board[${String(index)}] must be a tile id, integer token, or null`);
    return null;
  });
}

function parseProbeTupleMove(value: unknown, path: string, rows: number, cols: number, errors: string[]): Match3ProbeTupleMoveV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push(`${path} must be a move object`);
    return undefined;
  }
  const parseCoordinateObject = (point: unknown, pointPath: string): readonly [number, number] | undefined => {
    if (!isRecord(point)) {
      errors.push(`${pointPath} must be [row, col] or { row, col }`);
      return undefined;
    }
    const row = asInteger(point.row, `${pointPath}.row`, errors, 0);
    const col = asInteger(point.col, `${pointPath}.col`, errors, 0);
    if (row === undefined || col === undefined) return undefined;
    if (row >= rows || col >= cols) errors.push(`${pointPath} must be within bounds`);
    return [row, col] as const;
  };
  const parsePoint = (point: unknown, pointPath: string): readonly [number, number] | undefined => {
    if (isRecord(point)) return parseCoordinateObject(point, pointPath);
    if (!Array.isArray(point) || point.length !== 2) {
      errors.push(`${pointPath} must be [row, col] or { row, col }`);
      return undefined;
    }
    const row = asInteger(point[0], `${pointPath}[0]`, errors, 0);
    const col = asInteger(point[1], `${pointPath}[1]`, errors, 0);
    if (row === undefined || col === undefined) return undefined;
    if (row >= rows || col >= cols) errors.push(`${pointPath} must be within bounds`);
    return [row, col] as const;
  };
  const fromSource = value.from ?? value.a;
  const toSource = value.to ?? value.b;
  const from = parsePoint(fromSource, `${path}.from`);
  const to = parsePoint(toSource, `${path}.to`);
  if (!from || !to) return undefined;
  return { from, to };
}

function parseProbeIndexMove(value: unknown, path: string, maxIndexExclusive: number, errors: string[]): Match3ProbeIndexMoveV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push(`${path} must be an index move object`);
    return undefined;
  }
  const fromIndex = asInteger(value.fromIndex, `${path}.fromIndex`, errors, 0, maxIndexExclusive - 1);
  const toIndex = asInteger(value.toIndex, `${path}.toIndex`, errors, 0, maxIndexExclusive - 1);
  if (fromIndex === undefined || toIndex === undefined) return undefined;
  return { fromIndex, toIndex };
}

function parseProbeIndexes(value: unknown, path: string, maxIndexExclusive: number, errors: string[]): readonly number[] | undefined {
  if (value === undefined) return undefined;
  const raw = asArray(value, path, errors);
  if (!raw) return undefined;
  return raw
    .map((item, index) => asInteger(item, `${path}[${String(index)}]`, errors, 0, maxIndexExclusive - 1))
    .filter((item): item is number => item !== undefined);
}

function parseRequiredProbeIndexes(value: unknown, path: string, maxIndexExclusive: number, errors: string[]): readonly number[] {
  const parsed = parseProbeIndexes(value, path, maxIndexExclusive, errors);
  return parsed ?? [];
}

function parseProbeBoardCells(
  value: unknown,
  path: string,
  expectedSize: number,
  errors: string[],
): Match3ProbeBoardCellV1[] {
  const raw = asArray(value, path, errors);
  if (!raw) return [];
  const flattened = raw.every(Array.isArray) ? raw.flat() : raw;
  if (flattened.length !== expectedSize) {
    errors.push(`${path} length ${String(flattened.length)} must equal rows*cols ${String(expectedSize)}`);
  }
  return flattened.map((cell, index): Match3ProbeBoardCellV1 => {
    if (cell === null) return null;
    if (typeof cell === 'string' && cell.length > 0) return cell;
    if (typeof cell === 'number' && Number.isInteger(cell)) return cell;
    errors.push(`${path}[${String(index)}] must be a tile id, integer token, or null`);
    return null;
  });
}

function parseNullableProbeIndexMove(
  value: unknown,
  path: string,
  maxIndexExclusive: number,
  errors: string[],
): Match3ProbeIndexMoveV1 | null {
  if (value === null) return null;
  if (value === undefined) {
    errors.push(`${path} must be an index move object or null`);
    return null;
  }
  return parseProbeIndexMove(value, path, maxIndexExclusive, errors) ?? null;
}

function parseProbeResolutionProgress(value: unknown, path: string, errors: string[]): Match3ProbeResolutionProgressV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a progress object`);
    return undefined;
  }
  const scoreDelta = asInteger(value.scoreDelta, `${path}.scoreDelta`, errors);
  const score = asInteger(value.score, `${path}.score`, errors, 0);
  const collected = asInteger(value.collected, `${path}.collected`, errors, 0);
  const objectiveRemaining = asInteger(value.objectiveRemaining, `${path}.objectiveRemaining`, errors, 0);
  if (scoreDelta === undefined || score === undefined || collected === undefined || objectiveRemaining === undefined) return undefined;
  return { scoreDelta, score, collected, objectiveRemaining };
}

function parseProbeResolutionCascade(
  value: unknown,
  path: string,
  expectedSize: number,
  errors: string[],
): Match3ProbeResolutionCascadeV1 | undefined {
  if (!isRecord(value)) {
    errors.push(`${path} must be a cascade object`);
    return undefined;
  }
  const phase = asEnum(value.phase, `${path}.phase`, ['clear-refill'] as const, errors);
  const matchedIndexes = parseRequiredProbeIndexes(value.matchedIndexes, `${path}.matchedIndexes`, expectedSize, errors);
  const boardAfterClear = parseProbeBoardCells(value.boardAfterClear, `${path}.boardAfterClear`, expectedSize, errors);
  const cleared = asInteger(value.cleared, `${path}.cleared`, errors, 0);
  const progress = parseProbeResolutionProgress(value.progress, `${path}.progress`, errors);
  const refillChangedIndexes = parseRequiredProbeIndexes(value.refillChangedIndexes, `${path}.refillChangedIndexes`, expectedSize, errors);
  const boardAfterRefill = parseProbeBoardCells(value.boardAfterRefill, `${path}.boardAfterRefill`, expectedSize, errors);
  const repaintIndexes = parseRequiredProbeIndexes(value.repaintIndexes, `${path}.repaintIndexes`, expectedSize, errors);
  if (phase === undefined || cleared === undefined || progress === undefined) return undefined;
  return {
    phase,
    matchedIndexes,
    boardAfterClear,
    cleared,
    progress,
    refillChangedIndexes,
    boardAfterRefill,
    repaintIndexes,
  };
}

function parseProbeResolutionPlan(
  value: unknown,
  path: string,
  expectedSize: number,
  errors: string[],
): Match3ProbeResolutionPlanV1 | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value)) {
    errors.push(`${path} must be a resolution plan object`);
    return undefined;
  }
  const result = asEnum(value.result, `${path}.result`, ['accepted', 'rejected'] as const, errors);
  const fromIndex = asInteger(value.fromIndex, `${path}.fromIndex`, errors, 0, expectedSize - 1);
  const toIndex = asInteger(value.toIndex, `${path}.toIndex`, errors, 0, expectedSize - 1);
  const acceptedMove = parseNullableProbeIndexMove(value.acceptedMove, `${path}.acceptedMove`, expectedSize, errors);
  const rejectedMove = parseNullableProbeIndexMove(value.rejectedMove, `${path}.rejectedMove`, expectedSize, errors);
  const initialMatchedIndexes = parseRequiredProbeIndexes(value.initialMatchedIndexes, `${path}.initialMatchedIndexes`, expectedSize, errors);
  const postSwapBoard = parseProbeBoardCells(value.postSwapBoard, `${path}.postSwapBoard`, expectedSize, errors);
  const rawCascades = asArray(value.cascades, `${path}.cascades`, errors) ?? [];
  const cascades = rawCascades
    .map((cascade, index) => parseProbeResolutionCascade(cascade, `${path}.cascades[${String(index)}]`, expectedSize, errors))
    .filter((cascade): cascade is Match3ProbeResolutionCascadeV1 => cascade !== undefined);
  const repaintIndexes = parseRequiredProbeIndexes(value.repaintIndexes, `${path}.repaintIndexes`, expectedSize, errors);
  const finalBoard = parseProbeBoardCells(value.finalBoard, `${path}.finalBoard`, expectedSize, errors);
  const outcome = asEnum(value.outcome, `${path}.outcome`, ['win', 'lose', 'none'] as const, errors);
  if (result === undefined || fromIndex === undefined || toIndex === undefined || outcome === undefined) return undefined;
  return {
    result,
    fromIndex,
    toIndex,
    acceptedMove,
    rejectedMove,
    initialMatchedIndexes,
    postSwapBoard,
    cascades,
    repaintIndexes,
    finalBoard,
    outcome,
  };
}

function parseProbeObjectiveFlights(
  value: unknown,
  path: string,
  rows: number,
  cols: number,
  expectedSize: number,
  errors: string[],
): readonly Match3ProbeObjectiveFlightV1[] | undefined {
  if (value === undefined) return undefined;
  const raw = asArray(value, path, errors);
  if (!raw) return undefined;
  return raw
    .map((entry, index): Match3ProbeObjectiveFlightV1 | undefined => {
      const itemPath = `${path}[${String(index)}]`;
      if (!isRecord(entry)) {
        errors.push(`${itemPath} must be an objective flight object`);
        return undefined;
      }
      const sourceIndex = asInteger(entry.sourceIndex, `${itemPath}.sourceIndex`, errors, 0, expectedSize - 1);
      const row = asInteger(entry.row, `${itemPath}.row`, errors, 0, rows - 1);
      const col = asInteger(entry.col, `${itemPath}.col`, errors, 0, cols - 1);
      const source = isRect(entry.source, `${itemPath}.source`, errors);
      const target = isRect(entry.target, `${itemPath}.target`, errors);
      const durationMs = asInteger(entry.durationMs, `${itemPath}.durationMs`, errors, 1, 30000);
      const status = asEnum(
        entry.status,
        `${itemPath}.status`,
        ['scheduled', 'running', 'finished'] as const,
        errors,
      );
      if (sourceIndex !== undefined && row !== undefined && col !== undefined && sourceIndex !== indexOfCell(rows, cols, row, col)) {
        errors.push(`${itemPath}.sourceIndex must match row/col`);
      }
      if (
        sourceIndex === undefined ||
        row === undefined ||
        col === undefined ||
        source === undefined ||
        target === undefined ||
        durationMs === undefined ||
        status === undefined
      ) {
        return undefined;
      }
      return {
        sourceIndex,
        row,
        col,
        source,
        target,
        durationMs,
        status,
      };
    })
    .filter((entry): entry is Match3ProbeObjectiveFlightV1 => entry !== undefined);
}

export function validateMatch3ProbeStateV1(state: unknown): Match3ProbeStateValidationResult {
  const errors: string[] = [];
  const dedupe = new Set<string>();
  const dedupedPush = (msg: string): void => {
    pushError(errors, msg, dedupe);
  };
  if (!isRecord(state)) return failProbe(['probe state must be an object']);

  const rows = asInteger(state.rows, 'rows', errors, 1);
  const cols = asInteger(state.cols, 'cols', errors, 1);
  if (rows === undefined || cols === undefined) return failProbe(errors);
  const expectedSize = rows * cols;
  const board = normalizeProbeBoard(state.board, rows, cols, errors);
  const firstMove = parseProbeTupleMove(state.firstMove, 'firstMove', rows, cols, errors);
  const nextMove = parseProbeTupleMove(state.nextMove, 'nextMove', rows, cols, errors);
  const rawCellRects = asArray(state.cellRects, 'cellRects', errors);
  const cellRects: Match3ProbeCellRectV1[] = [];
  if (rawCellRects) {
    const seenCells = new Set<string>();
    rawCellRects.forEach((item, index) => {
      const path = `cellRects[${String(index)}]`;
      if (!isRecord(item)) {
        dedupedPush(`${path} must be an object`);
        return;
      }
      const tileIndex = asInteger(item.tileIndex, `${path}.tileIndex`, errors, 0, expectedSize - 1);
      const row = asInteger(item.row, `${path}.row`, errors, 0, rows - 1);
      const col = asInteger(item.col, `${path}.col`, errors, 0, cols - 1);
      const x = asNumber(item.x, `${path}.x`, errors);
      const y = asNumber(item.y, `${path}.y`, errors);
      const width = asNumber(item.width, `${path}.width`, errors, 0);
      const height = asNumber(item.height, `${path}.height`, errors, 0);
      if (tileIndex === undefined || row === undefined || col === undefined || x === undefined || y === undefined || width === undefined || height === undefined) return;
      if (width <= 0) dedupedPush(`${path}.width must be > 0`);
      if (height <= 0) dedupedPush(`${path}.height must be > 0`);
      const key = `${String(row)}:${String(col)}`;
      if (seenCells.has(key)) dedupedPush(`duplicate cell rect at ${key}`);
      seenCells.add(key);
      cellRects.push({ tileIndex, row, col, x, y, width, height });
    });
  }

  const rawTileLayerIds = asArray(state.tileLayerIds, 'tileLayerIds', errors);
  const tileLayerIds = rawTileLayerIds
    ? rawTileLayerIds
        .map((item, index) => asString(item, `tileLayerIds[${String(index)}]`, errors))
        .filter((item): item is string => item !== undefined)
    : [];
  const lastAcceptedMove = parseProbeIndexMove(state.lastAcceptedMove, 'lastAcceptedMove', expectedSize, errors);
  const acceptedMoveSeq = state.acceptedMoveSeq === undefined ? undefined : asInteger(state.acceptedMoveSeq, 'acceptedMoveSeq', errors, 0);
  const lastRejectedMove = parseProbeIndexMove(state.lastRejectedMove, 'lastRejectedMove', expectedSize, errors);
  const rejectedMoveSeq = state.rejectedMoveSeq === undefined ? undefined : asInteger(state.rejectedMoveSeq, 'rejectedMoveSeq', errors, 0);
  const lastRepaintIndexes = parseProbeIndexes(state.lastRepaintIndexes, 'lastRepaintIndexes', expectedSize, errors);
  const lastResolutionPlan = parseProbeResolutionPlan(state.lastResolutionPlan, 'lastResolutionPlan', expectedSize, errors);
  const staticCellIndexes = parseProbeIndexes(state.staticCellIndexes, 'staticCellIndexes', expectedSize, errors);
  const tutorialMaskIndexes = parseProbeIndexes(state.tutorialMaskIndexes, 'tutorialMaskIndexes', expectedSize, errors);
  const objectiveFlights = parseProbeObjectiveFlights(state.objectiveFlights, 'objectiveFlights', rows, cols, expectedSize, errors);
  const resolutionPhase = state.resolutionPhase === undefined
    ? undefined
    : asEnum(state.resolutionPhase, 'resolutionPhase', ['idle', 'swap', 'clear', 'refill', 'complete'] as const, errors);
  const objectiveRemaining = asInteger(state.objectiveRemaining, 'objectiveRemaining', errors, 0);
  const flowerStageProgress = state.flowerStageProgress === undefined ? undefined : asNumber(state.flowerStageProgress, 'flowerStageProgress', errors, 0);
  const tileCount = asInteger(state.tileCount, 'tileCount', errors, 0);
  const resolving = asBoolean(state.resolving, 'resolving', errors);
  const ctaShown = asBoolean(state.ctaShown, 'ctaShown', errors);

  if (errors.length > 0 || board.length !== expectedSize || objectiveRemaining === undefined || tileCount === undefined || resolving === undefined || ctaShown === undefined) {
    return failProbe(errors);
  }

  return {
    ok: true,
    value: {
      rows,
      cols,
      board,
      ...(firstMove === undefined ? {} : { firstMove }),
      ...(nextMove === undefined ? {} : { nextMove }),
      cellRects,
      tileLayerIds,
      ...(lastAcceptedMove === undefined ? {} : { lastAcceptedMove }),
      ...(acceptedMoveSeq === undefined ? {} : { acceptedMoveSeq }),
      ...(lastRejectedMove === undefined ? {} : { lastRejectedMove }),
      ...(rejectedMoveSeq === undefined ? {} : { rejectedMoveSeq }),
      ...(lastRepaintIndexes === undefined ? {} : { lastRepaintIndexes }),
      ...(lastResolutionPlan === undefined ? {} : { lastResolutionPlan }),
      ...(staticCellIndexes === undefined ? {} : { staticCellIndexes }),
      ...(tutorialMaskIndexes === undefined ? {} : { tutorialMaskIndexes }),
      ...(objectiveFlights === undefined ? {} : { objectiveFlights }),
      ...(resolutionPhase === undefined ? {} : { resolutionPhase }),
      objectiveRemaining,
      ...(flowerStageProgress === undefined ? {} : { flowerStageProgress }),
      tileCount,
      resolving,
      ctaShown,
    },
  };
}
